import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  buildCareSourceFileMetadata,
  buildCareSourceTextMetadata,
  CARE_SOURCE_CONTEXTS,
  CARE_SOURCE_TEXT_EXTRACTION_STATUSES,
} from './careSourceMetadata';
import { captureCareSourceForProcessing } from './careSourceProcessingClient';
import {
  buildExplicitProfilePlacementMetadata,
  PROFILE_PLACEMENT_MODES,
} from '../profileContract/profilePlacementMetadata';
import {
  DIRECT_PROFILE_KNOWLEDGE_PERSISTENCE_MODE,
  PROFILE_ORGANIZATION_MODES,
} from './directProfileKnowledgeModel';

const DEFAULT_DEPS = {};

const getCaptureCareSource = (deps = DEFAULT_DEPS) => (
  deps.captureCareSourceForProcessing || captureCareSourceForProcessing
);

const getUploadImageToStorage = (deps = DEFAULT_DEPS) => (
  deps.uploadImageToStorage || uploadImageToStorage
);

const getUploadOriginalFileToStorage = (deps = DEFAULT_DEPS) => (
  deps.uploadOriginalFileToStorage || uploadOriginalFileToStorage
);

const normalizeStorageFileName = (fileName = 'source') => (
  String(fileName || 'source')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  || 'source'
);

const buildFileIdentity = (file = {}) => {
  const value = [
    file.name || '',
    file.type || '',
    Number(file.size || 0),
    Number(file.lastModified || 0),
  ].join('|');
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const buildOriginalFileStoragePath = ({ childId = '', file = null } = {}) => (
  `careSources/documents/${childId}/${buildFileIdentity(file)}-${normalizeStorageFileName(file?.name)}`
);

export const uploadOriginalFileToStorage = ({ file, childId }) => {
  const storage = getStorage();
  const storagePath = buildOriginalFileStoragePath({ childId, file });
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, {
    ...(file?.type ? { contentType: file.type } : {}),
    customMetadata: {
      originalFileName: String(file?.name || ''),
      childId: String(childId || ''),
    },
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      null,
      reject,
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const uploadedAt = new Date().toISOString();
          resolve({
            url,
            type: 'document',
            filename: file?.name || '',
            fileName: file?.name || '',
            mimeType: file?.type || '',
            fileSize: Number(file?.size || 0),
            path: uploadTask.snapshot.ref.fullPath,
            storagePath: uploadTask.snapshot.ref.fullPath,
            uploadedAt,
          });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
};

export const saveFileCareSourceForProcessing = async ({
  childId = '',
  rawText = '',
  sourceType = 'unknown',
  sourceContext,
  textExtractionStatus,
  extractionError = null,
  file = null,
  profileChapterId = '',
  profileField = '',
  profilePlacementMode = '',
  originalFileAttachment = null,
  selectedDate = null,
  selectedLocalDate = null,
  sourceUseCase = '',
  reviewPolicy = '',
  importBatchId = '',
  importBatchOrder = null,
  deps = DEFAULT_DEPS,
} = {}) => {
  const captureCareSource = getCaptureCareSource(deps);
  const uploadOriginalFile = getUploadOriginalFileToStorage(deps);
  const preservedFile = originalFileAttachment || await uploadOriginalFile({ file, childId });

  return captureCareSource({
    childId,
    rawText,
    sourceType,
    sourceMetadata: {
      ...buildCareSourceFileMetadata({
        file,
        sourceContext,
        textExtractionStatus,
        extractionError,
        textLength: String(rawText || '').length,
        selectedDate,
        selectedLocalDate,
        sourceUseCase,
        reviewPolicy,
        importBatchId,
        importBatchOrder,
      }),
      documentAttachments: [preservedFile],
      ...buildExplicitProfilePlacementMetadata({ profileChapterId, profileField, profilePlacementMode }),
    },
  });
};

const uploadImageToStorage = ({ file, childId }) => {
  const storage = getStorage();
  const uploadId = `care-source-image-${Date.now()}`;
  const storageRef = ref(storage, `careSources/images/${childId}/${uploadId}_${file.name}`);
  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      null,
      reject,
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve([{
            url,
            type: 'image',
            filename: file.name,
            path: uploadTask.snapshot.ref.fullPath,
            uploadedAt: new Date().toISOString(),
          }]);
        } catch (err) {
          reject(err);
        }
      }
    );
  });
};

export const saveImageCareSourceFileForProcessing = async ({
  childId = '',
  file = null,
  sourceType = 'unknown',
  sourceContext = CARE_SOURCE_CONTEXTS.TIMELINE,
  profileChapterId = '',
  profileField = '',
  profilePlacementMode = '',
  selectedDate = null,
  selectedLocalDate = null,
  sourceUseCase = '',
  reviewPolicy = '',
  importBatchId = '',
  importBatchOrder = null,
  deps = DEFAULT_DEPS,
} = {}) => {
  const captureCareSource = getCaptureCareSource(deps);
  const uploadImage = getUploadImageToStorage(deps);
  const imageAttachments = await uploadImage({ file, childId });
  const mediaUrls = imageAttachments.map((item) => item.url).filter(Boolean);
  const isChapterScopedProfileImage = (
    profilePlacementMode === PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER
    && Boolean(String(profileChapterId || '').trim())
  );

  return captureCareSource({
    childId,
    rawText: '',
    sourceType,
    sourceMetadata: {
      ...buildCareSourceFileMetadata({
        file,
        sourceContext,
        textExtractionStatus: CARE_SOURCE_TEXT_EXTRACTION_STATUSES.PENDING,
        extractionError: {
          code: 'ocr-pending',
          message: 'Image text will be read after the source is saved.',
        },
        textLength: 0,
        selectedDate,
        selectedLocalDate,
        sourceUseCase,
        reviewPolicy,
        importBatchId,
        importBatchOrder,
      }),
      imageAttachments,
      mediaUrls,
      ...buildExplicitProfilePlacementMetadata({ profileChapterId, profileField, profilePlacementMode }),
      ...(isChapterScopedProfileImage ? {
        knowledgePersistenceMode: DIRECT_PROFILE_KNOWLEDGE_PERSISTENCE_MODE,
        profileOrganizationMode: PROFILE_ORGANIZATION_MODES.COMPLEX,
      } : {}),
    },
  });
};

export const saveTextCareSourceForProcessing = async ({
  childId = '',
  rawText = '',
  sourceType = 'unknown',
  sourceName = 'Typed note',
  inputMode = 'composer',
  selectedDate = null,
  selectedLocalDate = null,
  sourceContext,
  profileChapterId = '',
  profileField = '',
  profilePlacementMode = '',
  knowledgePersistenceMode = '',
  profileOrganizationMode = '',
  deps = DEFAULT_DEPS,
} = {}) => {
  const captureCareSource = getCaptureCareSource(deps);

  return captureCareSource({
    childId,
    rawText,
    sourceType,
    sourceMetadata: {
      ...buildCareSourceTextMetadata({
        sourceName,
        inputMode,
        selectedDate,
        selectedLocalDate,
        sourceContext,
        textLength: String(rawText || '').length,
      }),
      ...buildExplicitProfilePlacementMetadata({ profileChapterId, profileField, profilePlacementMode }),
      ...(knowledgePersistenceMode ? { knowledgePersistenceMode: String(knowledgePersistenceMode).trim() } : {}),
      ...(profileOrganizationMode ? { profileOrganizationMode: String(profileOrganizationMode).trim() } : {}),
    },
  });
};
